import type { QueryClient } from '@tanstack/react-query';
import type { AuthUser } from '@teo/backend';

import { buildLoginRedirectPath } from '../lib/authRedirect';
import { type AuthSessionInvalidatedDetail, takeWorkosLogoutUrl } from '../lib/authSession';

function goToLoginPage() {
  if (typeof window === 'undefined' || window.location.pathname === '/login') return;

  window.history.replaceState(
    null,
    '',
    buildLoginRedirectPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    ),
  );
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function applySessionInvalidation(
  detail: AuthSessionInvalidatedDetail | undefined,
  queryClient: QueryClient,
  setUser: (user: AuthUser | null) => void,
) {
  setUser(null);
  queryClient.clear();

  // Clearing our own tokens is not the end of the session. The AuthKit cookie
  // lives on WorkOS's domain, so an idle timeout that only cleared locally
  // would hand the next visitor to /login a fresh code without asking anyone
  // for a password, a passkey or a second factor. Leaving the SPA is the
  // point: WorkOS ends the session and redirects back to /login itself.
  const workosLogoutUrl = takeWorkosLogoutUrl();
  if (workosLogoutUrl && typeof window !== 'undefined') {
    window.location.assign(workosLogoutUrl);
    return;
  }

  if (detail?.navigateToLogin) goToLoginPage();
}

/**
 * Ends this tab's session and nothing that tabs share.
 *
 * For a sign-out that another tab made. That tab has removed the tokens. If it
 * signed out on purpose or ran out of idle time, it also took the WorkOS logout URL
 * and ended the vendor session. A tab whose page load failed removes the tokens and
 * does not end it. By the time this tab hears of it, the tokens, the activity stamp
 * and the URL in storage may belong to someone who signed in since. Reading or
 * writing any of them from here could end that person's session, so this tab only
 * forgets what it holds itself.
 */
export function applyLocalSignOut(
  queryClient: QueryClient,
  setUser: (user: AuthUser | null) => void,
) {
  setUser(null);
  queryClient.clear();
  goToLoginPage();
}
